import { useContext, useEffect, useRef } from 'react'

import { ErrorBoundary as ChatErrorBoundary } from '@chat/components/error-boundary'
import { logger } from '@chat/services/logger'
import { useForm, getFormProps } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { cn } from '@optechai/design-system'
import type { ActionFunctionArgs, LoaderFunctionArgs } from '@remix-run/node'
import {
  isRouteErrorResponse,
  useFetcher,
  useLoaderData,
  useRouteError,
} from '@remix-run/react'
import { setContext } from '@sentry/remix'
import { TRPCClientError } from '@trpc/client'
import { z } from 'zod'

import { CustomisationContext } from '@web/components/message/context'
import { MessageThread } from '@web/components/message/messages'
import { PoweredBy } from '@web/components/powered-by/powered-by'
import { SendBar } from '@web/components/send-bar/send-bar'
import {
  usePendingMessages,
  usePoll,
} from '@web/routes/__app/conversation.($ticketId)/utils'
import { uploadFile } from '@web/services/file/upload.server'

import type { TicketId } from '../../../server/src/models/ticket/ticket.dto'
import { FeatureFlagContext } from '../../../web/src/services/feature-flag'
import { ThreadShell } from '../components/shell'
import { validatePublicKey } from '../helpers/auth'
import { SessionJWT } from '../services/session/jwt.server'
import { trpc } from '../services/trpc/client'

export const loader = async (args: LoaderFunctionArgs) => {
  logger.log('GET /tickets/$publicKey/$ticketId', {
    url: args.request.url,
    params: args.params,
  })

  const { request } = args
  const url = new URL(request.url)

  const { publicKey, ticketId } = args.params

  const customerFirstName = url.searchParams.get('customer_first_name')
  const customerLastName = url.searchParams.get('customer_last_name')
  const session = url.searchParams.get('session')

  if (!publicKey) {
    throw new Response('Public key must be provided', { status: 400 })
  }

  if (!ticketId) {
    throw new Response('Ticket ID must be provided', { status: 400 })
  }

  const jwt = url.searchParams.get('session')
  const sessionJWT = new SessionJWT(ticketId as TicketId, jwt)
  const error = sessionJWT.verify()

  if (error) {
    logger.error('Invalid session token (invalid access)', {
      publicKey,
      ticketId,
    })
    // throw new Response('Invalid session token', { status: 403 })
  }

  const { subscriberId } = await validatePublicKey(publicKey)

  // Set the subscriber context for Sentry
  setContext('subscriber', {
    id: subscriberId,
    publicKey,
  })

  try {
    const [
      { messages, status, events, customer, actions, ticket, config },
      featureFlags,
    ] = await Promise.all([
      trpc.chatWidget.getMessageHistoryStatusAndConfig.query(
        {
          publicKey,
          ticketId,
        },
        { context: { publicKey, subscriberId } },
      ),
      trpc.subscriberSetup.getFeatureFlags.query(
        { subscriberId },
        { context: { publicKey, subscriberId } },
      ),
    ])

    return {
      customerFirstName: customerFirstName || customer?.firstName,
      customerLastName: customerLastName || customer?.lastName,
      messages,
      session,
      status,
      actions,
      ticket,
      publicKey,
      events,
      featureFlags,
      allowSendOnEscalation:
        config && config.integrationType === 'ChatWidget'
          ? config.allowSendOnEscalation
          : false,
    }
  } catch (error) {
    if (error instanceof TRPCClientError && error.data?.code === 'NOT_FOUND') {
      throw new Response(error.message, { status: 404 })
    } else {
      logger.error('Error loading ticket', { publicKey, ticketId, error })
      throw error
    }
  }
}

const NewMessageSchema = z.object({
  customerFirstName: z.string().optional(),
  customerLastName: z.string().optional(),
  message: z.string().optional().default(''),
  file: z.instanceof(File).optional(),
})

export const action = async (args: ActionFunctionArgs) => {
  // await getDataFromSession(args)

  logger.log('POST /ticket/$publicKey/$ticketId', {
    url: args.request.url,
    params: args.params,
  })

  if (args.params.ticketId === undefined) {
    throw new Error('Remote ID must be provided')
  }

  if (args.params.publicKey === undefined) {
    throw new Error('Public key must be provided')
  }

  const { publicKey, ticketId } = args.params

  const formData = await args.request.formData()
  const submission = parseWithZod(formData, { schema: NewMessageSchema })

  if (submission.status !== 'success') {
    logger.error('Invalid form submission', {
      submission,
    })
    return submission.reply()
  }

  /**
   * Note this is to allow the optional value to be submitted but
   * not cause the form to error.
   *
   * Without this check when the form revalidates it could
   * cause the whole page to redirect.
   */
  if (!submission.payload.message) {
    return submission.reply()
  }

  try {
    const maybeFile = await uploadFile(submission.value.file)

    const { subscriberId } = await validatePublicKey(publicKey)
    await trpc.chatWidget.addMessageToTicket.mutate(
      {
        ...submission.value,
        publicKey,
        attachments: maybeFile ? [maybeFile] : [],
        ticketId,
      },
      { context: { publicKey, subscriberId } },
    )
    return null
  } catch (error) {
    if (error instanceof TRPCClientError && error.data?.code === 'NOT_FOUND') {
      throw new Response(error.message, { status: 404 })
    } else {
      throw error
    }
  }
}

const ACTIVE_POLLING_INTERVAL = 2_000
const IDLE_POLLING_INTERVAL = 20_000

export default function Index() {
  const {
    messages: rawMessages,
    status,
    ticket,
    actions,
    events,
    customerFirstName,
    customerLastName,
    session,
    featureFlags,
    allowSendOnEscalation,
  } = useLoaderData<typeof loader>()

  const messages = usePendingMessages(rawMessages)
  const pollingState = usePoll(
    messages,
    ACTIVE_POLLING_INTERVAL,
    IDLE_POLLING_INTERVAL,
  )

  const actionData = useFetcher<typeof action>({ key: 'sendChat' })
  const [form] = useForm<z.infer<typeof NewMessageSchema>>({
    onValidate: ({ formData }) => {
      return parseWithZod(formData, {
        schema: NewMessageSchema,
      })
    },
    lastResult: actionData.state === 'idle' ? actionData.data : null,
    defaultValue: {
      customerFirstName,
      customerLastName,
      message: '',
    },
  })

  useEffect(() => {
    if (session) {
      window.parent.postMessage(
        {
          type: 'persistLorikeetSession',
          session: session,
        },
        '*',
      )
    }
    if (ticket.id) {
      window.parent.postMessage(
        {
          type: 'persistLorikeetTicketId',
          ticketId: ticket.id,
        },
        '*',
      )
    }
  }, [ticket.id, session])

  const scrollRef = useRef<HTMLDivElement>(null)

  // Scroll to the bottom when new messages are added.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages.length, pollingState])

  const showSendBar = !(
    status === 'EscalatedFailed' ||
    (status === 'Escalated' && !allowSendOnEscalation)
  )

  const customisation = useContext(CustomisationContext)
  const bottomOffset =
    showSendBar && !customisation?.useSubtleBranding
      ? 11 // Sendbar + PoweredBy components
      : showSendBar
        ? 4.875 // Sendbar component only
        : customisation?.useSubtleBranding
          ? 0 // No bottom offset, subtle branding at top
          : 2.25 // PoweredBy component only

  return (
    <FeatureFlagContext.Provider value={featureFlags}>
      <ThreadShell ticket={ticket}>
        <div
          className={cn(
            'w-full flex-1 self-start overflow-y-auto scroll-smooth',
          )}
          ref={scrollRef}
          style={{
            maxBlockSize: `max(34.5rem, 100% - ${4 + bottomOffset}rem)`,
            marginBlockEnd: `${bottomOffset}rem`,
          }}
        >
          <MessageThread
            actions={actions}
            hasEscalated={events.some(
              (event) => event.escalationReason !== undefined,
            )}
            invertMessageAlignment
            isLoading={
              pollingState === 'active' &&
              // This is a hack to avoid showing the loading spinner when we're
              // not actually processing the ticket anymore (e.g. because it's
              // been escalated)
              (status === 'Unprocessed' ||
                status === 'Processing' ||
                status === 'Responded')
            }
            messages={messages}
          />
        </div>
        <actionData.Form
          className="fixed bottom-0 w-full"
          encType="multipart/form-data"
          method="POST"
          {...getFormProps(form)}
        >
          {customerFirstName && (
            <input
              defaultValue={customerFirstName}
              name="customerFirstName"
              type="hidden"
            />
          )}
          {customerLastName && (
            <input
              defaultValue={customerLastName}
              name="customerLastName"
              type="hidden"
            />
          )}
          {showSendBar && <SendBar allowEmptySubmit={false} />}
          {!customisation?.useSubtleBranding && <PoweredBy />}
        </actionData.Form>
      </ThreadShell>
    </FeatureFlagContext.Provider>
  )
}

/**
 * Reserverd for route error handling
 */
export const ErrorBoundary = () => {
  const error = useRouteError()
  const isRouteError = isRouteErrorResponse(error)

  useEffect(() => {
    window.parent.postMessage(
      {
        type: 'error',
        error: isRouteError ? 'UNKNOWN_PUBLIC_KEY' : 'FATAL_ERROR',
      },
      '*',
    )
  }, [])

  if (isRouteError && error.status === 404) {
    return (
      <ChatErrorBoundary error="UNKNOWN_PUBLIC_KEY" title="Configuration error">
        We don't recognise this chat instance. Looks like a misconfiguration.
      </ChatErrorBoundary>
    )
  }

  if (isRouteError && error.status === 403) {
    return (
      <ChatErrorBoundary error="INVALID_SESSION" title="Invalid session">
        The session token is invalid or missing. Viewing this page is not
        allowed.
      </ChatErrorBoundary>
    )
  }

  return (
    <ChatErrorBoundary error="FATAL_ERROR" title="Something went wrong">
      We're not sure what went wrong, but the chat instance is in a bad state.
    </ChatErrorBoundary>
  )
}
